/**
 * Pass-Through Space Calculator Business Logic Module for Murphy Door Configurator
 *
 * PURPOSE: Calculates pass-through space for door installations using PPD dimension data.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI updates through event publishing (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure BL)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * Business Logic Functions:
 * - Pass-through space calculations with door geometry processing
 * - Fraction conversion utilities with precision handling
 * - Input validation and error handling for unsupported configurations
 * - Universal calculator interface for all door types
 * - Mathematical accuracy with 0.1 inch precision requirement
 *
 * All external communication flows through md-config-core-conductor.js conductor
 *
 * Dependencies:
 * - md-config-core-conductor.js (PPD delegation, dimension extraction, UI delegation)
 * - md-config-core-event-bus.js (event system)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor orchestration)
 */

/**
 * Module uses scoped pattern for Shopify compatibility
 *
 * BOOTSTRAP PATTERN: Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
 * (Golden Standard: md-config-bl-material-selector.js)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    SPACE_CALCULATION: 100, // ms - full calculation must complete within 100ms
    FRACTION_CONVERSION: 10, // ms - fraction conversion
    COORDINATE_UPDATE: 50, // ms - coordinateSpaceUpdate
  });

  const SPACE_CALC_CONSTANTS = Object.freeze({
    PRECISION_TOLERANCE: 0.1, // inches - 0.1 inch precision requirement
    SUPPORTED_DEPTHS: Object.freeze({
      IN_SWING: Object.freeze([6.5, 8.25, 10, 12]), // 6-1/2", 8-1/4", 10", 12"
      OUT_SWING: Object.freeze([5.25, 6.5, 8.25, 10, 12]), // 5-1/4", 6-1/2", 8-1/4", 10", 12"
    }),
    // In-swing pass-through no longer uses a flat reduction (was W - 6). It is
    // sheet B24 (FINAL, R. Cooley 2026-09-25), see computeInSwingPassThrough:
    //   W - (jamb + 0.5) - (casingWidth - 1.5)
    // casingWidth is the "-1 RULE" casing: 4-3/4in when the rule is met and the
    // casing is not wide-rule-exempt (Fluted), else 3-3/4in.
    CASING_WIDTH: Object.freeze({
      STANDARD: 3.75, // 3-3/4"
      WIDE: 4.75, // 4-3/4" under the "-1 RULE"
    }),
    // Standard jamb (2x4 wall, 1/2in drywall). Used when no jamb is selected or
    // the selected one cannot be parsed, and as the "-1 RULE" threshold.
    STANDARD_JAMB_DEPTH: 4.5625, // 4-9/16"
  });

  const ERROR_MESSAGES = Object.freeze({
    INVALID_WIDTH: 'Door width must be a positive number',
    INVALID_DEPTH:
      'Door depth is required and must be a valid number or fractional string',
    INVALID_TYPE: 'Door type must be either "in-swing" or "out-swing"',
    CALCULATION_FAILED:
      'Space calculation failed due to invalid input parameters',
    CONVERSION_FAILED: 'Fraction conversion failed due to invalid input format',
    NOT_BOOTSTRAPPED: 'Space calculator called before bootstrap complete',
    MISSING_DATA: 'Missing data parameter for space calculation',
    MISSING_STATE: 'Missing state parameter for space calculation',
    MISSING_DEPENDENCIES: 'Missing dependencies for space update coordination',
  });

  // Lookup tables for door specifications.
  //
  // SOURCE OF TRUTH: engineering's "Door Measurement Calculator" sheet
  // (R. Cooley, rev. 2026-09-25), scope FMD2 SINGLE door. Mirrored for the
  // non-configurator surfaces in assets/pass-through-diagrams.js; both copies are
  // pinned to the sheet by tests/assets/cooley-sheet-oracle.test.js and
  // tests/assets/pass-through-math-parity.test.js.
  //
  // IN-SWING wall clearance ("Back-Side Wall Clearance", sheet B23) is a
  // function of door depth AND jamb depth -- it is no longer a depth-keyed
  // table. See computeInSwingWallClearance below. The old depth-only series
  // (3/4 / 1-3/4 / 3-3/4 / 5-3/4, from max(0, floor(depth - 6.5) + 0.75))
  // assumed a 6.5in jamb and under-reported by up to 1-5/16in.
  // Do NOT use the FRENCH 1/2" reveal series from assets/french-door-widget.js
  // here -- French is outside the sheet's scope and keeps its own formula.
  const IN_SWING_SPECS = Object.freeze({
    6.5: Object.freeze({ protrusion: 2 }), // 6-1/2"
    8.25: Object.freeze({ protrusion: 3.75 }), // 8-1/4"
    10: Object.freeze({ protrusion: 5.5 }), // 10"
    12: Object.freeze({ protrusion: 7.5 }), // 12"
  });

  // Out-swing pass-through = W - (depth + 3.5)  (sheet B21, out-swing arm).
  // Was depth + 3 until 2026-09-25, which over-reported by 1/2in at every depth.
  const OUT_SWING_SPECS = Object.freeze({
    5.25: Object.freeze({ reduction: 8.75, protrusion: 0.75 }), // 5-1/4"
    6.5: Object.freeze({ reduction: 10, protrusion: 2 }), // 6-1/2" - ADDED FOR PRODUCT COMPATIBILITY
    8.25: Object.freeze({ reduction: 11.75, protrusion: 3.75 }), // 8-1/4"
    10: Object.freeze({ reduction: 13.5, protrusion: 5.5 }), // 10"
    12: Object.freeze({ reduction: 15.5, protrusion: 7.5 }), // 12"
  });

  /**
   * In-swing back-side wall clearance, sheet B23 (rev. 2026-09-25), measured
   * from the outside edge of the jamb to the back corner of the open door --
   * the same quantity the blueprint labels "Wall clearance needed from jamb"
   * (confirmed by R. Cooley).
   *
   *   depth == 8-1/4 AND jamb > 4-9/16 : depth + 0.75 - jamb - 1.4375
   *   otherwise                        : depth - jamb - 1.4375
   *
   * Clamped at 0: a negative value means the door clears inside the jamb, and
   * 0 is rendered by every consumer as "no clearance" exactly as before.
   * Callers without a known jamb pass the standard 4-9/16in.
   */
  function computeInSwingWallClearance(depth, jamb) {
    const j = Number.isFinite(jamb) ? jamb : SPACE_CALC_CONSTANTS.STANDARD_JAMB_DEPTH;
    const raw =
      depth === 8.25 && j > SPACE_CALC_CONSTANTS.STANDARD_JAMB_DEPTH
        ? depth + 0.75 - j - 1.4375
        : depth - j - 1.4375;
    // Round to 1/16in so float noise (e.g. 1.9999999) never reaches a label.
    return Math.max(0, Math.round(raw * 16) / 16);
  }

  /**
   * Swing clearance, sheet B24 (rev. 2026-09-25 evening), engineering's
   * definition: "the distance from the wall to the furthest point on the door
   * when open".
   *
   *   In-Swing:  casingWidth == 3-3/4 ? W - 1.25 : W - 2.25
   *   Out-Swing: W - 4
   *
   * casingWidth is the SAME "-1 RULE" casing computeInSwingPassThrough
   * resolves (4-3/4 when the rule is met and the front casing is not Fluted),
   * so a Fluted door under the rule gets W - 1.25 -- as the sheet's formula
   * states.
   *
   * 🚨 NOT the wall clearance (computeInSwingWallClearance, sheet B25). Wall
   * clearance is room needed beside the jamb; swing clearance is how far the
   * open door reaches out from the wall. Two quantities, two callouts.
   *
   * Rounded to 1/16in so float noise never reaches a label.
   *
   * @param {number} doorWidth - nominal door width (sheet Width_Nom)
   * @param {'in'|'out'} swing
   * @param {number} [casingWidth] - in-swing only: 3.75 or 4.75
   * @returns {number}
   */
  function computeSwingClearance(doorWidth, swing, casingWidth) {
    const raw = swing === 'in'
      ? doorWidth - (casingWidth === SPACE_CALC_CONSTANTS.CASING_WIDTH.WIDE ? 2.25 : 1.25)
      : doorWidth - 4;
    return Math.round(raw * 16) / 16;
  }

  /**
   * In-swing pass-through, sheet B24 (R. Cooley, confirmed FINAL 2026-09-25):
   *
   *   W - (jamb + 0.5) - (casingWidth - 1.5)
   *
   * casingWidth reuses the blueprint's casing resolution: the shared "-1 RULE"
   * predicate (core utils meetsMinusOneRule; for in-swing, jamb > 4-9/16) picks
   * 4-3/4in, UNLESS the front casing is wide-rule-exempt (Fluted, vendor-milled,
   * always 3-3/4in). The exemption flag is resolved by the conductor from the
   * trim profile (doorDimensions.casingWideRuleExempt); a missing casing is
   * treated as Standard (not exempt).
   *
   * Deliberately NOT the sheet's newer B10 "only Flat gets 4-3/4" formula --
   * engineering has not marked row 10 Ready to Deploy.
   *
   * At the standard 4-9/16in jamb: W - 7-5/16. Rounded to 1/16in so float
   * noise never reaches a label.
   *
   * @param {number} doorWidth
   * @param {number} jamb - inches; callers resolve the standard fallback
   * @param {boolean} casingWideRuleExempt
   * @returns {{passThrough: number, casingWidth: number}}
   */
  function computeInSwingPassThrough(doorWidth, jamb, casingWideRuleExempt) {
    const utils = window.MurphyDoorConfigurator.utils;
    const ruleMet =
      !!utils &&
      typeof utils.meetsMinusOneRule === 'function' &&
      utils.meetsMinusOneRule({ swing: 'Inswing', jambIn: jamb });
    const casingWidth =
      ruleMet && casingWideRuleExempt !== true
        ? SPACE_CALC_CONSTANTS.CASING_WIDTH.WIDE
        : SPACE_CALC_CONSTANTS.CASING_WIDTH.STANDARD;
    const raw = doorWidth - (jamb + 0.5) - (casingWidth - 1.5);
    return { passThrough: Math.round(raw * 16) / 16, casingWidth };
  }

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // DEDUPLICATION GUARD: Prevents double-fire when SELECTION_BROWSING and APPLY_SELECTION
  // both trigger handleSpaceCalculationUpdates at the same millisecond for dimension categories.
  // Tracks the last calculation key + timestamp to skip redundant recalculations.
  let _lastSpaceCalcKey = null;
  let _lastSpaceCalcTimestamp = 0;
  const SPACE_CALC_DEDUP_WINDOW = 50; // ms - skip duplicate within this window

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[SpaceCalculator] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[SpaceCalculator] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    // Subscribe to bootstrap completion at module load time (race protection)
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[SpaceCalculator] Bootstrap complete event received');
        bootstrapComplete = true;
        isInitialized = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[SpaceCalculator] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[SpaceCalculator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }

        // Publish module ready event
        publishEvent(EVENTS.MODULE_SPACE_CALCULATOR_READY, {
          module: 'spaceCalculator',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    // PATTERN 5: EVENT REPLAY READY - Subscribe to cleanup/shutdown
    const unsubCleanup = CoreEventBus.subscribe(
      EVENTS.MODULE_SPACE_CALCULATOR_CLEANUP,
      () => {
        isDev() && console.log('[SpaceCalculator] Cleanup event received');
        cleanup();
      }
    );
    cleanupHandlers.push(unsubCleanup);

    isDev() &&
      console.log(
        '[SpaceCalculator] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING HELPER (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_SPACE_CALCULATION_SLOW, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
      });
    }
  }

  // ============================================================================
  // CLEANUP FUNCTION (Pattern 15)
  // ============================================================================
  function cleanup() {
    isDev() && console.log('[SpaceCalculator] Cleaning up resources');
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (e) {
        /* already cleaned */
      }
    });
    cleanupHandlers = [];
    pendingCalls = [];
    isInitialized = false;
    bootstrapComplete = false;
    _lastSpaceCalcKey = null;
    _lastSpaceCalcTimestamp = 0;
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.spaceCalculator = {
    /**
     * Convert fractional strings to decimal inches with precision handling.
     * PPD Integration: The PPD provides depth as a numeric value (e.g., 6.5)
     * via extractedDimensions.depth, so this method accepts BOTH numbers
     * (PPD path) and fractional strings (legacy path).
     *
     * @param {string|number} fractionStr - Fraction string (e.g., "6-1/2", "3/4", "10") or numeric value (e.g., 6.5)
     * @returns {number} Decimal value in inches
     */
    fractionToDecimal(fractionStr) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'spaceCalculator.fractionToDecimal';

      try {
        isDev() &&
          console.log(
            '[SpaceCalculator] Converting fraction to decimal',
            fractionStr
          );

        // PPD numeric path: PPD provides depth as a number (e.g., 6.5 from
        // extractedDimensions.depth). Return as-is since it's already the
        // decimal value we need - no string parsing required.
        if (typeof fractionStr === 'number') {
          if (!isFinite(fractionStr) || fractionStr <= 0) {
            throw new Error(ERROR_MESSAGES.CONVERSION_FAILED);
          }

          const executionTime = window.performance.now() - startTime;

          isDev() &&
            console.log(
              '[SpaceCalculator] Depth already numeric (PPD path), returning as-is:',
              fractionStr
            );

          publishEvent(EVENTS.BUSINESS_FRACTION_CONVERSION_COMPLETED, {
            input: fractionStr,
            output: fractionStr,
            inputType: 'number',
            executionTime,
            source: METHOD_SIGNATURE,
          });

          trackPerformance('FRACTION_CONVERSION', executionTime);
          return fractionStr;
        }

        // Legacy string path: fractional strings like "6-1/2", "3/4", "10"
        if (!fractionStr || typeof fractionStr !== 'string') {
          throw new Error(ERROR_MESSAGES.CONVERSION_FAILED);
        }

        const parts = fractionStr.trim().split('-');
        let decimal = 0;

        if (parts.length === 2) {
          // Handle format like "6-1/2"
          decimal += parseInt(parts[0]);
          if (parts[1].includes('/')) {
            const [num, den] = parts[1].split('/').map(Number);
            decimal += num / den;
          }
        } else if (fractionStr.includes('/')) {
          // Handle format like "3/4"
          const [num, den] = fractionStr.split('/').map(Number);
          decimal = num / den;
        } else {
          // Handle whole numbers
          decimal = parseFloat(fractionStr);
        }

        const executionTime = window.performance.now() - startTime;

        isDev() &&
          console.log('[SpaceCalculator] Conversion result:', {
            input: fractionStr,
            output: decimal,
            executionTime: `${executionTime.toFixed(2)}ms`,
          });

        // Publish conversion completed event
        publishEvent(EVENTS.BUSINESS_FRACTION_CONVERSION_COMPLETED, {
          input: fractionStr,
          output: decimal,
          executionTime,
          source: METHOD_SIGNATURE,
        });

        trackPerformance('FRACTION_CONVERSION', executionTime);
        return decimal;
      } catch (error) {
        isDev() &&
          console.error(
            '[SpaceCalculator] Fraction conversion failed:',
            error.message
          );
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_VALIDATION_FAILED, {
          error: error.message,
          field: 'fractionStr',
          value: fractionStr,
          source: METHOD_SIGNATURE,
        });
        throw error;
      }
    },

    /**
     * Convert decimal back to fractional display with common fractions
     * @param {number} decimal - Decimal value in inches
     * @returns {string} Formatted fraction string with quote mark
     */
    decimalToFraction(decimal) {
      const startTime = window.performance.now();

      try {
        isDev() &&
          console.log(
            '[SpaceCalculator] Converting decimal to fraction',
            decimal
          );

        if (typeof decimal !== 'number' || isNaN(decimal)) {
          throw new Error(ERROR_MESSAGES.CONVERSION_FAILED);
        }

        const whole = Math.floor(decimal);
        const fraction = decimal - whole;

        if (fraction === 0) {
          return whole.toString() + '"';
        }

        // Common fractions with precision tolerance
        // Sixteenths included since 2026-09-25: jamb-dependent wall clearance
        // (sheet B23) lands on sixteenths for the Custom jamb variants
        // (e.g. 8-1/4 depth, 3-5/8 jamb -> 3-3/16), which eighths would have
        // fallen through to a "3.2" decimal display.
        const fractions = {
          0.25: '1/4',
          0.5: '1/2',
          0.75: '3/4',
          0.125: '1/8',
          0.375: '3/8',
          0.625: '5/8',
          0.875: '7/8',
          0.0625: '1/16',
          0.1875: '3/16',
          0.3125: '5/16',
          0.4375: '7/16',
          0.5625: '9/16',
          0.6875: '11/16',
          0.8125: '13/16',
          0.9375: '15/16',
        };

        const closest = Object.keys(fractions).reduce((prev, curr) =>
          Math.abs(curr - fraction) < Math.abs(prev - fraction) ? curr : prev
        );

        if (
          Math.abs(closest - fraction) <
          SPACE_CALC_CONSTANTS.PRECISION_TOLERANCE / 10
        ) {
          const result =
            whole > 0
              ? `${whole}-${fractions[closest]}"`
              : `${fractions[closest]}"`;

          const executionTime = window.performance.now() - startTime;
          isDev() &&
            console.log('[SpaceCalculator] Fraction result:', {
              input: decimal,
              output: result,
              executionTime: `${executionTime.toFixed(2)}ms`,
            });

          return result;
        }

        const result = decimal.toFixed(1) + '"';
        const executionTime = window.performance.now() - startTime;

        isDev() &&
          console.log('[SpaceCalculator] Decimal result:', {
            input: decimal,
            output: result,
            executionTime: `${executionTime.toFixed(2)}ms`,
          });

        return result;
      } catch (error) {
        isDev() &&
          console.error(
            '[SpaceCalculator] Decimal conversion failed:',
            error.message
          );
        throw error;
      }
    },

    /**
     * Calculate pass-through specifications for in-swing doors.
     * PPD Integration: doorDepth accepts both numbers and fractional strings.
     *
     * @param {number} doorWidth - Door width in inches
     * @param {string|number} doorDepth - Door depth as string (e.g., "6-1/2") or number (e.g., 6.5)
     * @param {number} [jambDepth] - Selected jamb depth in inches; standard 4-9/16 when absent
     * @param {boolean} [casingWideRuleExempt] - true for a Fluted front casing (always 3-3/4in); absent = Standard
     * @returns {Object} Calculation results with pass-through, clearance, and protrusion
     */
    calculateInSwingDoor(doorWidth, doorDepth, jambDepth, casingWideRuleExempt) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'spaceCalculator.calculateInSwingDoor';

      try {
        isDev() &&
          console.log('[SpaceCalculator] Calculating in-swing door specs', {
            doorWidth,
            doorDepth,
          });

        // Validate inputs
        this.validateCalculationInputs(doorWidth, doorDepth, 'in-swing');

        const depthDecimal = this.fractionToDecimal(doorDepth);

        // Find the closest matching depth from known specifications
        const availableDepths = SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.IN_SWING;
        const closestDepth = availableDepths.reduce((prev, curr) =>
          Math.abs(curr - depthDecimal) < Math.abs(prev - depthDecimal)
            ? curr
            : prev
        );

        // Use closest depth specs, or interpolate/estimate if needed
        const specs = IN_SWING_SPECS[closestDepth];
        const effectiveJamb = Number.isFinite(jambDepth)
          ? jambDepth
          : SPACE_CALC_CONSTANTS.STANDARD_JAMB_DEPTH;
        const casingExempt = casingWideRuleExempt === true;
        const { passThrough, casingWidth } = computeInSwingPassThrough(
          doorWidth,
          effectiveJamb,
          casingExempt
        );
        const wallClearance = computeInSwingWallClearance(
          closestDepth,
          effectiveJamb
        );
        const swingClearance = computeSwingClearance(doorWidth, 'in', casingWidth);

        const result = {
          doorType: 'In-Swing',
          doorWidth: doorWidth,
          doorDepth: doorDepth,
          // The jamb the wall clearance was computed FROM, so a cached payload
          // can be checked against the current selection (see
          // md-config-bl-dimension-calculator.js spaceMatchesSelection).
          jambDepth: effectiveJamb,
          // The casing inputs pass-through was computed FROM (sheet B24), for
          // the same cache-staleness check as jambDepth.
          casingWideRuleExempt: casingExempt,
          casingWidth: casingWidth,
          passThrough: passThrough,
          wallClearance: wallClearance,
          // Sheet B24 swing clearance -- NOT wall clearance, see
          // computeSwingClearance.
          swingClearance: swingClearance,
          protrusion: specs.protrusion,
          passThroughDisplay: this.decimalToFraction(passThrough),
          wallClearanceDisplay: wallClearance
            ? this.decimalToFraction(wallClearance)
            : 'NA',
          protrusionDisplay: this.decimalToFraction(specs.protrusion),
        };

        const executionTime = window.performance.now() - startTime;

        isDev() &&
          console.log('[SpaceCalculator] In-swing calculation result:', {
            ...result,
            executionTime: `${executionTime.toFixed(2)}ms`,
          });

        // PATTERN 12: Performance tracking
        trackPerformance('SPACE_CALCULATION', executionTime);

        return result;
      } catch (error) {
        isDev() &&
          console.error(
            '[SpaceCalculator] In-swing calculation failed:',
            error.message
          );
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_FAILED, {
          error: error.message,
          doorWidth,
          doorDepth,
          doorType: 'in-swing',
          source: METHOD_SIGNATURE,
        });
        throw error;
      }
    },

    /**
     * Calculate pass-through specifications for out-swing doors.
     * PPD Integration: doorDepth accepts both numbers and fractional strings.
     *
     * @param {number} doorWidth - Door width in inches
     * @param {string|number} doorDepth - Door depth as string (e.g., "5-1/4") or number (e.g., 5.25)
     * @returns {Object} Calculation results with pass-through and protrusion
     */
    calculateOutSwingDoor(doorWidth, doorDepth) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'spaceCalculator.calculateOutSwingDoor';

      try {
        isDev() &&
          console.log('[SpaceCalculator] Calculating out-swing door specs', {
            doorWidth,
            doorDepth,
          });

        // Validate inputs
        this.validateCalculationInputs(doorWidth, doorDepth, 'out-swing');

        const depthDecimal = this.fractionToDecimal(doorDepth);

        // Find the closest matching depth from known specifications
        const availableDepths = SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.OUT_SWING;
        const closestDepth = availableDepths.reduce((prev, curr) =>
          Math.abs(curr - depthDecimal) < Math.abs(prev - depthDecimal)
            ? curr
            : prev
        );

        // Use closest depth specs, or interpolate/estimate if needed
        const specs = OUT_SWING_SPECS[closestDepth];
        const passThrough = doorWidth - specs.reduction;
        // Sheet B24 out-swing arm: W - 4, no jamb or casing term.
        const swingClearance = computeSwingClearance(doorWidth, 'out');

        const result = {
          doorType: 'Out-Swing',
          doorWidth: doorWidth,
          doorDepth: doorDepth,
          passThrough: passThrough,
          swingClearance: swingClearance,
          protrusion: specs.protrusion,
          passThroughDisplay: this.decimalToFraction(passThrough),
          protrusionDisplay: this.decimalToFraction(specs.protrusion),
        };

        const executionTime = window.performance.now() - startTime;

        isDev() &&
          console.log('[SpaceCalculator] Out-swing calculation result:', {
            ...result,
            executionTime: `${executionTime.toFixed(2)}ms`,
          });

        // PATTERN 12: Performance tracking
        trackPerformance('SPACE_CALCULATION', executionTime);

        return result;
      } catch (error) {
        isDev() &&
          console.error(
            '[SpaceCalculator] Out-swing calculation failed:',
            error.message
          );
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_FAILED, {
          error: error.message,
          doorWidth,
          doorDepth,
          doorType: 'out-swing',
          source: METHOD_SIGNATURE,
        });
        throw error;
      }
    },

    /**
     * Universal calculator interface that determines appropriate calculation based on door type.
     * PPD Integration: doorDepth accepts both numbers and fractional strings.
     *
     * Implements 5-stage event lifecycle:
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {number} doorWidth - Door width in inches
     * @param {string|number} doorDepth - Door depth as string (e.g., "6-1/2") or number (e.g., 6.5)
     * @param {string} doorType - Either "in-swing" or "out-swing"
     * @param {number} [jambDepth] - Selected jamb depth in inches (in-swing pass-through + wall clearance only)
     * @param {boolean} [casingWideRuleExempt] - Fluted front casing (in-swing pass-through only)
     * @returns {Object} Calculation results
     */
    calculateDoorSpecs(doorWidth, doorDepth, doorType, jambDepth, casingWideRuleExempt) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'spaceCalculator.calculateDoorSpecs';

      try {
        isDev() &&
          console.log('[SpaceCalculator] Universal calculation request', {
            doorWidth,
            doorDepth,
            doorType,
          });

        // ================================================================
        // STAGE 1: START - Announce operation beginning (Pattern 3)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_STARTED, {
          doorWidth,
          doorDepth,
          doorType,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate inputs (Pattern 3)
        // ================================================================
        this.validateCalculationInputs(doorWidth, doorDepth, doorType);

        // ================================================================
        // STAGE 3: PROGRESS - Execute calculation (Pattern 3)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_PROGRESS, {
          stage: 'calculating',
          doorType,
          source: METHOD_SIGNATURE,
        });

        const type = doorType.toLowerCase().replace(/[\s-]/g, '');
        let result;

        switch (type) {
          case 'inswing':
            result = this.calculateInSwingDoor(
              doorWidth,
              doorDepth,
              jambDepth,
              casingWideRuleExempt
            );
            break;
          case 'outswing':
            result = this.calculateOutSwingDoor(doorWidth, doorDepth);
            break;
          default:
            throw new Error(ERROR_MESSAGES.INVALID_TYPE);
        }

        const executionTime = window.performance.now() - startTime;

        isDev() &&
          console.log(
            `[SpaceCalculator] Universal calculation completed in ${executionTime.toFixed(2)}ms`
          );

        // ================================================================
        // STAGE 4: SUCCESS - Announce completion (Pattern 3)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_COMPLETED, {
          doorWidth,
          doorDepth,
          doorType,
          result,
          executionTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // PATTERN 12: Performance tracking
        trackPerformance('SPACE_CALCULATION', executionTime);

        return result;
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Announce failure (Pattern 3, 14)
        // ================================================================
        const executionTime = window.performance.now() - startTime;
        isDev() &&
          console.error(
            '[SpaceCalculator] Universal calculation failed:',
            error.message
          );
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_FAILED, {
          error: error.message,
          doorWidth,
          doorDepth,
          doorType,
          executionTime,
          source: METHOD_SIGNATURE,
        });
        throw error;
      }
    },

    /**
     * Validate calculation inputs for consistency and completeness.
     * PPD Integration: doorDepth accepts both numbers (PPD extractedDimensions.depth)
     * and fractional strings (legacy path) for backward compatibility.
     *
     * @param {number} doorWidth - Door width in inches
     * @param {string|number} doorDepth - Door depth as string (e.g., "6-1/2") or number (e.g., 6.5)
     * @param {string} doorType - Door type string
     * @throws {Error} If validation fails
     */
    validateCalculationInputs(doorWidth, doorDepth, doorType) {
      const METHOD_SIGNATURE = 'spaceCalculator.validateCalculationInputs';

      isDev() &&
        console.log('[SpaceCalculator] Validating calculation inputs', {
          doorWidth,
          doorDepth,
          doorDepthType: typeof doorDepth,
          doorType,
        });

      if (typeof doorWidth !== 'number' || doorWidth <= 0 || isNaN(doorWidth)) {
        const error = new Error(ERROR_MESSAGES.INVALID_WIDTH);
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_VALIDATION_FAILED, {
          error: error.message,
          field: 'doorWidth',
          value: doorWidth,
          source: METHOD_SIGNATURE,
        });
        throw error;
      }

      // PPD provides depth as number (extractedDimensions.depth), legacy provides as string
      // Accept both: number must be positive and finite, string must be non-empty
      const isValidNumber =
        typeof doorDepth === 'number' && isFinite(doorDepth) && doorDepth > 0;
      const isValidString =
        typeof doorDepth === 'string' && doorDepth.trim() !== '';

      if (!doorDepth || (!isValidNumber && !isValidString)) {
        const error = new Error(ERROR_MESSAGES.INVALID_DEPTH);
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_VALIDATION_FAILED, {
          error: error.message,
          field: 'doorDepth',
          value: doorDepth,
          valueType: typeof doorDepth,
          source: METHOD_SIGNATURE,
        });
        throw error;
      }

      if (!doorType || typeof doorType !== 'string' || doorType.trim() === '') {
        const error = new Error(ERROR_MESSAGES.INVALID_TYPE);
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_VALIDATION_FAILED, {
          error: error.message,
          field: 'doorType',
          value: doorType,
          source: METHOD_SIGNATURE,
        });
        throw error;
      }

      isDev() && console.log('[SpaceCalculator] All calculation inputs valid');
    },

    /**
     * The `diagram` block every space consumer reads (the pass-through tooltip,
     * the blueprint's pass-through / wall-clearance / swing-clearance callouts,
     * the mobile metrics dashboard). ONE builder so the published payload and
     * the no-handing scenarios below can never disagree on shape.
     *
     * @param {Object} spaceResult - calculateInSwingDoor / calculateOutSwingDoor result
     * @param {Object} dims - conductor.getDoorDimensions() shape (handling, handlingLabel)
     * @returns {Object} diagram
     */
    buildSpaceDiagram(spaceResult, dims) {
      const effectiveDimensions = dims || {};
      return {
        doorType: spaceResult.doorType, // 'In-Swing' | 'Out-Swing'
        // handling is swing-only ('in-swing'), because the variant enhancer
        // drops handedness when it builds extractedDimensions. The diagram
        // needs left vs right too, so handlingLabel carries the raw variant
        // title ('Left Hand Inswing'). Nullable -- the diagram falls back to
        // its neutral placeholder rather than guessing a side.
        handling: effectiveDimensions.handling,
        handlingLabel: effectiveDimensions.handlingLabel || null,
        doorWidth: spaceResult.doorWidth,
        doorDepth: spaceResult.doorDepth,
        // In-swing only; undefined on out-swing (no wall clearance there).
        jambDepth: spaceResult.jambDepth,
        // In-swing only: the casing inputs of sheet B24 pass-through.
        casingWideRuleExempt: spaceResult.casingWideRuleExempt,
        casingWidth: spaceResult.casingWidth,
        passThrough: spaceResult.passThrough,
        passThroughDisplay: spaceResult.passThroughDisplay,
        // wallClearance is undefined on out-swing results by design -- an
        // out-swing door swings away from the wall, so there is none.
        wallClearance: spaceResult.wallClearance,
        wallClearanceDisplay: spaceResult.wallClearanceDisplay,
        // Sheet B24, BOTH swings (unlike wallClearance). Read by the
        // blueprint's 'swing-clearance' callout; never re-derived there.
        swingClearance: spaceResult.swingClearance,
      };
    },

    /**
     * BOTH swings' diagrams for the applied width/depth/jamb/casing, for a
     * surface that must state "if out-swing / if in-swing" BEFORE the customer
     * has picked a Door Handing (the mobile metrics dashboard under the Door
     * Depth slider). coordinateSpaceUpdate cannot serve that: it requires a
     * handing and publishes nothing without one.
     *
     * Same formulas, same owner -- calculateInSwingDoor / calculateOutSwingDoor
     * -- shaped by buildSpaceDiagram, so a consumer feeding these into
     * dimensionCalculator.computeCallouts gets the numbers the blueprint would
     * print once the handing is chosen. Publishes no BUSINESS_SPACE_CALCULATION_*
     * event on the happy path (a what-if must not look like a real calculation
     * to the pass-through tooltip or the blueprint; only the per-call fraction
     * conversion bookkeeping every calculation emits), and a swing whose depth
     * is not in
     * SUPPORTED_DEPTHS (in-swing at 5-1/4in) is null rather than
     * nearest-depth-guessed: that door cannot be ordered in-swing at all.
     *
     * @param {Object|null} dims - conductor.getDoorDimensions()
     * @returns {{inSwing: Object|null, outSwing: Object|null}}
     */
    computeSwingScenarios(dims) {
      const none = { inSwing: null, outSwing: null };
      if (!dims || !Number.isFinite(dims.width)) return none;
      let depth = null;
      try {
        depth =
          typeof dims.depth === 'number'
            ? dims.depth
            : dims.depth === null || dims.depth === undefined
              ? null
              : this.fractionToDecimal(dims.depth);
      } catch (e) {
        depth = null;
      }
      if (!Number.isFinite(depth) || depth <= 0) return none;

      const supports = (list) =>
        list.some((d) => Math.abs(d - depth) < 0.01);
      const coreUtils = window.MurphyDoorConfigurator.utils;
      const jamb =
        dims.jambLabel &&
        coreUtils &&
        typeof coreUtils.parseFractionalInches === 'function'
          ? coreUtils.parseFractionalInches(dims.jambLabel)
          : null;
      const base = { handling: null, handlingLabel: null };

      const scenario = (fn) => {
        try {
          return fn();
        } catch (e) {
          isDev() &&
            console.warn('[SpaceCalculator] swing scenario failed:', e.message);
          return null;
        }
      };

      return {
        inSwing: supports(SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.IN_SWING)
          ? scenario(() =>
              this.buildSpaceDiagram(
                this.calculateInSwingDoor(
                  dims.width,
                  depth,
                  Number.isFinite(jamb) ? jamb : null,
                  dims.casingWideRuleExempt === true
                ),
                base
              )
            )
          : null,
        outSwing: supports(SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.OUT_SWING)
          ? scenario(() =>
              this.buildSpaceDiagram(
                this.calculateOutSwingDoor(dims.width, depth),
                base
              )
            )
          : null,
      };
    },

    /**
     * Get supported door depths for validation and UI display
     * @param {string} doorType - Either "in-swing" or "out-swing"
     * @returns {Array} Array of supported depths as decimal values
     */
    getSupportedDepths(doorType) {
      const type = doorType.toLowerCase().replace(/[\s-]/g, '');

      switch (type) {
        case 'inswing':
          return SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.IN_SWING;
        case 'outswing':
          return SPACE_CALC_CONSTANTS.SUPPORTED_DEPTHS.OUT_SWING;
        default:
          return [];
      }
    },

    /**
     * Get calculation constants for validation and testing
     * @returns {Object} Calculation constants object
     */
    getCalculationConstants() {
      return { ...SPACE_CALC_CONSTANTS };
    },

    /**
     * ENHANCED EVENT-DRIVEN READINESS TRACKING
     * Tracks combined applied + browsing selections for the 3 required categories
     * This allows space calculation to be ready BEFORE all 3 categories are applied
     *
     * PPD-FIRST ARCHITECTURE: Category names are sourced from PPD's dimensionCategoryIndex
     * (O(1) lookup, immutable) instead of hardcoded magic strings. This ensures the
     * readiness check always matches the actual category IDs from the Shopify backend.
     *
     * Required Categories: [widthCategory, depthCategory, handlingCategory] from PPD
     * Triggers readiness when 2 of 3 categories have APPLIED or BROWSING selections
     *
     * @param {Object} appliedSelections - Current applied selections from state manager
     * @param {Object} browsingSelection - Current browsing selection from state event (optional)
     * @param {Object} conductor - Conductor reference for PPD access (CCP delegation)
     * @returns {Object} Readiness status with category tracking
     */
    assessSpaceCalculationReadiness(
      appliedSelections,
      browsingSelection = null,
      conductor = null
    ) {
      const METHOD_SIGNATURE =
        'spaceCalculator.assessSpaceCalculationReadiness';
      isDev() &&
        console.log(
          `[SpaceCalculator] ${METHOD_SIGNATURE}: Assessing readiness with browsing context`
        );

      // PATTERN 7 & 9: PPD-FIRST via conductor delegation (O(1) lookup)
      // This eliminates hardcoded magic strings and ensures category names always match
      // the actual Shopify backend configuration (allRequiredCategoryIdsArray).
      let requiredCategories;
      if (conductor) {
        const dimensionIndex = conductor.getDimensionCategoryIndex();
        if (dimensionIndex) {
          requiredCategories = [
            dimensionIndex.widthCategory, // 'Width'
            dimensionIndex.depthCategory, // 'Door Depth' (from PPD Layer 4)
            dimensionIndex.handlingCategory, // 'Door Handing' (from PPD Layer 4)
          ];
          isDev() &&
            console.log(
              `[SpaceCalculator] PPD-FIRST category names:`,
              requiredCategories
            );
        } else {
          isDev() &&
            console.warn(
              `[SpaceCalculator] dimensionCategoryIndex not available from PPD - falling back to PPD defaults`
            );
          requiredCategories = ['Width', 'Door Depth', 'Door Handing'];
        }
      } else {
        isDev() &&
          console.warn(
            `[SpaceCalculator] No conductor provided - using PPD default category names`
          );
        requiredCategories = ['Width', 'Door Depth', 'Door Handing'];
      }
      const appliedRequiredCategories = [];
      const browsingRequiredCategories = [];
      const satisfiedCategories = []; // Categories with either applied OR browsing selections
      const missingCategories = [];

      // Check which required categories have APPLIED selections
      for (const categoryId of requiredCategories) {
        let hasApplied = false;
        let hasBrowsing = false;

        if (appliedSelections && appliedSelections[categoryId]) {
          appliedRequiredCategories.push(categoryId);
          hasApplied = true;
          isDev() &&
            console.log(
              `[SpaceCalculator] Readiness: ${categoryId} has applied selection`
            );
        }

        // Check if current browsing selection is for this category
        if (browsingSelection && browsingSelection.categoryId === categoryId) {
          browsingRequiredCategories.push(categoryId);
          hasBrowsing = true;
          isDev() &&
            console.log(
              `[SpaceCalculator] Readiness: ${categoryId} has browsing selection`
            );
        }

        // Category is satisfied if it has either applied OR browsing selection
        if (hasApplied || hasBrowsing) {
          satisfiedCategories.push(categoryId);
        } else {
          missingCategories.push(categoryId);
          isDev() &&
            console.log(
              `[SpaceCalculator] Readiness: ${categoryId} missing any selection`
            );
        }
      }

      const appliedCount = appliedRequiredCategories.length;
      const browsingCount = browsingRequiredCategories.length;
      const satisfiedCount = satisfiedCategories.length;
      const isReady = satisfiedCount >= 2; // Ready when 2 of 3 have ANY selection (applied or browsing)
      const isComplete = appliedCount === 3; // Complete only when all 3 are applied

      const readinessStatus = {
        isReady,
        isComplete,
        appliedCount,
        browsingCount,
        satisfiedCount,
        totalRequired: requiredCategories.length,
        appliedCategories: appliedRequiredCategories,
        browsingCategories: browsingRequiredCategories,
        satisfiedCategories: satisfiedCategories,
        missingCategories: missingCategories,
        readinessPercentage: Math.round(
          (satisfiedCount / requiredCategories.length) * 100
        ),
      };

      isDev() &&
        console.log(
          `[SpaceCalculator] Enhanced readiness status:`,
          readinessStatus
        );

      return readinessStatus;
    },

    /**
     * Simulates door dimensions by combining applied selections with a browsing selection
     * This allows space calculation to work when 2/3 categories are applied and 1 is browsing
     *
     * PATTERN 7 & 8: Uses conductor delegation (getProductByVariantId, getProductFromDictionary)
     * instead of directly accessing PPD. No raw data access.
     *
     * @param {Object} currentDimensions - Current door dimensions from applied selections
     * @param {Object} browsingSelection - Browsing selection data {categoryId, productId, variantId}
     * @param {Object} conductor - Conductor reference for accessing product data
     * @returns {Object} Simulated dimensions combining applied + browsing selections
     */
    simulateDimensionsWithBrowsing(
      currentDimensions,
      browsingSelection,
      conductor
    ) {
      const METHOD_SIGNATURE = 'spaceCalculator.simulateDimensionsWithBrowsing';
      isDev() &&
        console.log(
          `[SpaceCalculator] ${METHOD_SIGNATURE}: Simulating dimensions with browsing selection`
        );

      try {
        // Start with current applied dimensions
        const simulatedDimensions = { ...currentDimensions };

        // PATTERN 7 & 9: Get browsing product via conductor delegation (NOT direct PPD access)
        let browsingProduct = null;

        // Handle case where productId is null/undefined/string"undefined" but variantId exists
        if (
          (!browsingSelection.productId ||
            browsingSelection.productId === 'undefined') &&
          browsingSelection.variantId
        ) {
          isDev() &&
            console.log(
              `[SpaceCalculator] ${METHOD_SIGNATURE}: ProductId is null/undefined, using conductor.getProductByVariantId for variant ${browsingSelection.variantId}`
            );

          // PATTERN 7: Use conductor delegation O(1) lookup instead of direct PPD iteration
          browsingProduct = conductor.getProductByVariantId(
            browsingSelection.variantId
          );

          if (browsingProduct) {
            isDev() &&
              console.log(
                `[SpaceCalculator] ${METHOD_SIGNATURE}: Found product via conductor.getProductByVariantId for variant ${browsingSelection.variantId}`
              );
          }
        } else {
          // PATTERN 9: Use conductor delegation for product lookup
          browsingProduct = conductor.getProductFromDictionary(
            browsingSelection.productId
          );
        }

        if (!browsingProduct) {
          isDev() &&
            console.warn(
              `[SpaceCalculator] ${METHOD_SIGNATURE}: Could not find browsing product (productId: ${browsingSelection.productId}, variantId: ${browsingSelection.variantId})`
            );
          return currentDimensions;
        }

        // Get the specific variant if provided
        let browsingVariant = browsingProduct;
        if (browsingSelection.variantId && browsingProduct.variants) {
          browsingVariant = browsingProduct.variants.find(
            (v) => v.id == browsingSelection.variantId
          );
          if (!browsingVariant) {
            isDev() &&
              console.warn(
                `[SpaceCalculator] ${METHOD_SIGNATURE}: Could not find browsing variant ${browsingSelection.variantId}`
              );
            browsingVariant = browsingProduct;
          }
        }

        isDev() &&
          console.log(
            `[SpaceCalculator] ${METHOD_SIGNATURE}: Processing browsing selection for category ${browsingSelection.categoryId}`
          );

        // DRY APPROACH: Reuse the conductor's existing extractDimensionFromSelection method
        // Instead of duplicating the parsing logic, delegate to the conductor
        const browsingSelectionForExtraction = {
          productId: browsingProduct.id,
          variantId: browsingSelection.variantId,
        };

        // PPD-FIRST: Get dimension category names from PPD via conductor delegation (O(1) lookup)
        // This eliminates hardcoded magic strings - category names come from the immutable
        // dimensionCategoryIndex built in data-pipeline Layer 4.
        const dimensionIndex = conductor.getDimensionCategoryIndex();
        if (!dimensionIndex) {
          isDev() &&
            console.warn(
              `[SpaceCalculator] dimensionCategoryIndex not available - cannot map browsing category`
            );
          return currentDimensions;
        }

        // Extract the dimension value using the conductor's proven logic
        // PPD-FIRST: Using dimensionIndex category names instead of hardcoded strings
        let extractedValue = null;
        const categoryId = browsingSelection.categoryId;

        // Browsing a jamb: carry its title so the preview's in-swing wall
        // clearance tracks the hovered jamb (sheet B23 depends on it).
        if (
          categoryId === (dimensionIndex.jambCategory || 'Jamb Depth') &&
          browsingVariant &&
          browsingVariant !== browsingProduct && // a variant, not the product title
          typeof browsingVariant.title === 'string'
        ) {
          simulatedDimensions.jambLabel = browsingVariant.title;
          return simulatedDimensions;
        }

        // Browsing a front casing: in-swing pass-through subtracts the casing
        // width (sheet B24), and a Fluted casing never widens. The conductor
        // owns the title -> trim-profile -> wideRuleExempt resolution.
        if (
          categoryId === (dimensionIndex.frontCasingCategory || 'Front Casing') &&
          typeof conductor.resolveCasingWideRuleExempt === 'function'
        ) {
          // null = title is not a recognised casing SHAPE (e.g. a finish
          // swatch shown in the casing tab) -- keep the applied value rather
          // than silently flipping a Fluted door to Standard mid-hover.
          const exempt = conductor.resolveCasingWideRuleExempt(
            browsingProduct.title
          );
          if (typeof exempt === 'boolean') {
            simulatedDimensions.casingWideRuleExempt = exempt;
          }
          return simulatedDimensions;
        }

        if (categoryId === dimensionIndex.widthCategory) {
          extractedValue = conductor.extractDimensionFromSelection(
            browsingSelectionForExtraction,
            'width',
            categoryId
          );
          if (extractedValue) {
            simulatedDimensions.width = extractedValue;
            isDev() &&
              console.log(
                `[SpaceCalculator] ${METHOD_SIGNATURE}: Updated width to ${extractedValue} via conductor extraction`
              );
          } else {
            isDev() &&
              console.warn(
                `[SpaceCalculator] PPD dimension extraction returned null for width`,
                {
                  productId: browsingProduct.id,
                  variantId: browsingSelection.variantId,
                  categoryId,
                }
              );
          }
        } else if (categoryId === dimensionIndex.depthCategory) {
          extractedValue = conductor.extractDimensionFromSelection(
            browsingSelectionForExtraction,
            'depth',
            categoryId
          );
          if (extractedValue) {
            simulatedDimensions.depth = extractedValue;
            isDev() &&
              console.log(
                `[SpaceCalculator] ${METHOD_SIGNATURE}: Updated depth to ${extractedValue} via conductor extraction`
              );
          } else {
            isDev() &&
              console.warn(
                `[SpaceCalculator] PPD dimension extraction returned null for depth`,
                {
                  productId: browsingProduct.id,
                  variantId: browsingSelection.variantId,
                  categoryId,
                }
              );
          }
        } else if (categoryId === dimensionIndex.handlingCategory) {
          extractedValue = conductor.extractDimensionFromSelection(
            browsingSelectionForExtraction,
            'handling',
            categoryId
          );
          if (extractedValue) {
            simulatedDimensions.handling = extractedValue;
            isDev() &&
              console.log(
                `[SpaceCalculator] ${METHOD_SIGNATURE}: Updated handling to ${extractedValue} via conductor extraction`
              );
          } else {
            isDev() &&
              console.warn(
                `[SpaceCalculator] PPD dimension extraction returned null for handling`,
                {
                  productId: browsingProduct.id,
                  variantId: browsingSelection.variantId,
                  categoryId,
                }
              );
          }
        } else {
          isDev() &&
            console.log(
              `[SpaceCalculator] ${METHOD_SIGNATURE}: Category ${categoryId} not relevant for space calculation`
            );
        }

        isDev() &&
          console.log(
            `[SpaceCalculator] ${METHOD_SIGNATURE}: Simulated dimensions:`,
            simulatedDimensions
          );
        return simulatedDimensions;
      } catch (error) {
        isDev() &&
          console.error(
            `[SpaceCalculator] ${METHOD_SIGNATURE}: Error simulating dimensions:`,
            error
          );
        return currentDimensions; // Fallback to current dimensions
      }
    },

    /**
     * Coordinates space calculation updates with UI display integration.
     * Follows the same pattern as shipping calculator's coordinateShippingUpdate.
     *
     * Implements 5-stage event lifecycle:
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {Object} data - Selection data from state change
     * @param {string} state - Selection state (browsing/applied)
     * @param {Object} dependencies - Module dependencies (state manager, UI updater, event bus)
     * @returns {Object} Result with success status and space calculation data
     */
    coordinateSpaceUpdate(data, state, dependencies) {
      const startTime = performance.now();
      const METHOD_SIGNATURE = 'spaceCalculator.coordinateSpaceUpdate';

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(`[SpaceCalculator] ${ERROR_MESSAGES.NOT_BOOTSTRAPPED}`);
        pendingCalls.push({
          fn: this.coordinateSpaceUpdate,
          context: this,
          args: [data, state, dependencies],
        });
        return {
          success: false,
          error: ERROR_MESSAGES.NOT_BOOTSTRAPPED,
          queued: true,
        };
      }

      // DEDUP GUARD: For dimension category selections (Width, Door Depth, Door Handing),
      // both SELECTION_BROWSING and APPLY_SELECTION fire at the same millisecond.
      // Skip the redundant second call within the dedup window.
      const dedupKey = `${data?.categoryId}_${data?.productId}_${data?.variantId}`;
      const now = performance.now();
      if (
        dedupKey === _lastSpaceCalcKey &&
        now - _lastSpaceCalcTimestamp < SPACE_CALC_DEDUP_WINDOW
      ) {
        isDev() &&
          console.log(
            `[SpaceCalculator] Skipping duplicate coordinateSpaceUpdate (${(now - _lastSpaceCalcTimestamp).toFixed(1)}ms since last, state=${state})`
          );
        return {
          success: true,
          message: 'Skipped duplicate calculation',
          deduplicated: true,
        };
      }
      _lastSpaceCalcKey = dedupKey;
      _lastSpaceCalcTimestamp = now;

      isDev() &&
        console.log('[SpaceCalculator] coordinateSpaceUpdate called', {
          data,
          state,
        });

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning (Pattern 3)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_STARTED, {
          data: { categoryId: data?.categoryId, productId: data?.productId },
          state,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate parameters (Pattern 3, 14)
        // ================================================================
        if (!data) {
          throw new Error(ERROR_MESSAGES.MISSING_DATA);
        }

        if (!state) {
          throw new Error(ERROR_MESSAGES.MISSING_STATE);
        }

        if (!dependencies) {
          throw new Error(ERROR_MESSAGES.MISSING_DEPENDENCIES);
        }

        // ================================================================
        // STAGE 3: PROGRESS - Execute business logic (Pattern 3)
        // ================================================================
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_PROGRESS, {
          stage: 'assessing_readiness',
          source: METHOD_SIGNATURE,
        });

        // ENHANCED: Assess space calculation readiness with browsing context
        const appliedSelections = dependencies.conductor.getAppliedSelections();

        // Extract browsing selection from the data parameter if it's a BROWSING state
        let browsingSelection = null;
        if (state === dependencies.SELECTION_STATES.BROWSING && data) {
          browsingSelection = {
            categoryId: data.categoryId,
            productId: data.productId,
            variantId: data.variantId,
          };
          isDev() &&
            console.log(
              '[SpaceCalculator] Using browsing selection for readiness assessment:',
              browsingSelection
            );
        }

        const readinessStatus = this.assessSpaceCalculationReadiness(
          appliedSelections,
          browsingSelection,
          dependencies.conductor // PPD-FIRST: Pass conductor for dimensionCategoryIndex access
        );
        isDev() &&
          console.log('[SpaceCalculator] Readiness status:', readinessStatus);

        // PATTERN 10: Publish readiness event for UI coordination
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_READINESS, {
          readinessStatus,
          source: METHOD_SIGNATURE,
        });

        // Check if we're ready based on 2/3 categories rule (applied OR browsing)
        if (!readinessStatus.isReady) {
          isDev() &&
            console.log(
              '[SpaceCalculator] Not ready yet, hiding display.',
              `Need ${readinessStatus.totalRequired - readinessStatus.satisfiedCount} more category selections`
            );

          // PATTERN 10: UI update via dependency injection (conductor provides uiUpdater)
          if (dependencies.uiUpdater) {
            dependencies.uiUpdater.batchUpdate({
              passThroughSpace: {
                isVisible: false,
                width: null,
                depth: null,
                handling: null,
              },
            });
          }

          return {
            success: true,
            message:
              'Space calculation hidden - not ready (need 2/3 categories)',
            hasRequiredData: false,
            readinessStatus,
          };
        }

        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_PROGRESS, {
          stage: 'extracting_dimensions',
          source: METHOD_SIGNATURE,
        });

        // Extract door dimensions from current selection state (includes browsing context!)
        const doorDimensions = dependencies.conductor.getDoorDimensions();
        isDev() &&
          console.log(
            '[SpaceCalculator] Door dimensions extracted',
            doorDimensions
          );

        // For browsing state, we need to simulate the dimensions with the browsing selection
        let effectiveDimensions = doorDimensions;
        if (
          state === dependencies.SELECTION_STATES.BROWSING &&
          browsingSelection
        ) {
          isDev() &&
            console.log(
              '[SpaceCalculator] Simulating dimensions with browsing selection context'
            );
          effectiveDimensions = this.simulateDimensionsWithBrowsing(
            doorDimensions,
            browsingSelection,
            dependencies.conductor
          );
          isDev() &&
            console.log(
              '[SpaceCalculator] Effective dimensions with browsing:',
              effectiveDimensions
            );
        }

        // Determine if we have sufficient data to calculate and display space
        const hasRequiredDimensions =
          effectiveDimensions &&
          effectiveDimensions.width &&
          effectiveDimensions.depth &&
          effectiveDimensions.handling;

        if (!hasRequiredDimensions) {
          // UX POLISH: When 2/3 categories are selected but we're still missing
          // dimension(s), keep PTS hidden with "PTS" placeholder. No preliminary
          // prompt -- the calculated value appears only when all 3 are ready.
          isDev() &&
            console.log(
              '[SpaceCalculator] Missing dimensions - keeping PTS hidden',
              {
                effectiveDimensions,
                readinessStatus,
              }
            );

          if (dependencies.uiUpdater) {
            dependencies.uiUpdater.batchUpdate({
              passThroughSpace: {
                isVisible: false,
              },
            });
          }

          return {
            success: true,
            message: 'Space calculation hidden - missing dimensions',
            hasRequiredData: false,
            readinessStatus,
          };
        }

        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_PROGRESS, {
          stage: 'calculating',
          source: METHOD_SIGNATURE,
        });

        // Calculate pass-through space using calculateDoorSpecs method with effective dimensions
        // Jamb depth feeds in-swing wall clearance (sheet B26, was B23) and
        // in-swing pass-through (sheet B24). It arrives as the
        // raw variant title ('6-9/16 in.') and is parsed with the Phase 0 parser
        // the blueprint uses, so both read the same number. Unparseable or
        // absent (e.g. "Custom (Our Concierge Will Call You)") -> null, and
        // calculateInSwingDoor falls back to the standard 4-9/16in jamb.
        const coreUtils = window.MurphyDoorConfigurator.utils;
        const parsedJamb =
          effectiveDimensions.jambLabel &&
          coreUtils &&
          typeof coreUtils.parseFractionalInches === 'function'
            ? coreUtils.parseFractionalInches(effectiveDimensions.jambLabel)
            : null;

        // Jamb also feeds in-swing pass-through (sheet B24), together with the
        // front casing's Fluted exemption, which the conductor resolves into
        // casingWideRuleExempt (true/false, or null when no casing is applied ->
        // treated as Standard).
        const spaceResult = this.calculateDoorSpecs(
          effectiveDimensions.width,
          effectiveDimensions.depth,
          effectiveDimensions.handling,
          Number.isFinite(parsedJamb) ? parsedJamb : null,
          effectiveDimensions.casingWideRuleExempt === true
        );

        isDev() &&
          console.log(
            '[SpaceCalculator] Space calculation result',
            spaceResult
          );

        // Prepare space data for UI display
        //
        // The `diagram` block is ADDITIVE and exists so the pass-through tooltip
        // (md-config-pass-through-space-tooltip-initializer.js) can annotate its
        // door diagram from the numbers THIS module already calculated, instead of
        // recalculating them from raw dimensions. CLAUDE.md's "Pass-Through Math"
        // footgun exists precisely because these numbers got re-derived in
        // multiple places and drifted -- so they are forwarded, never redone.
        // Existing consumers read isVisible/width/depth/handling and are
        // unaffected by the extra key.
        const spaceDisplayData = {
          isVisible: true,
          width: spaceResult.passThrough
            ? spaceResult.passThrough.toFixed(1)
            : null,
          depth: null, // Depth not calculated in current implementation
          handling: effectiveDimensions.handling,
          diagram: this.buildSpaceDiagram(spaceResult, effectiveDimensions),
        };

        // PATTERN 10: Delegate UI updates through provided UI module
        if (dependencies.uiUpdater) {
          dependencies.uiUpdater.batchUpdate({
            passThroughSpace: spaceDisplayData,
          });
        }

        // ================================================================
        // STAGE 4: SUCCESS - Announce completion (Pattern 3)
        // ================================================================
        const executionTime = performance.now() - startTime;

        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_COMPLETED, {
          data: spaceDisplayData,
          timestamp: Date.now(),
          executionTime,
          source: METHOD_SIGNATURE,
        });

        // PATTERN 12: Performance tracking
        trackPerformance('COORDINATE_UPDATE', executionTime);

        isDev() &&
          console.log(
            `[SpaceCalculator] coordinateSpaceUpdate completed in ${executionTime.toFixed(2)}ms`
          );

        return {
          success: true,
          spaceData: spaceDisplayData,
          executionTime,
          hasRequiredData: true,
        };
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED - Announce failure (Pattern 3, 14)
        // ================================================================
        const executionTime = performance.now() - startTime;
        isDev() &&
          console.error(
            '[SpaceCalculator] coordinateSpaceUpdate error:',
            error
          );

        // Fallback: hide display on error
        if (dependencies?.uiUpdater) {
          try {
            dependencies.uiUpdater.batchUpdate({
              passThroughSpace: {
                isVisible: false,
                width: null,
                depth: null,
                handling: null,
              },
            });
          } catch (fallbackError) {
            isDev() &&
              console.error(
                '[SpaceCalculator] Fallback UI update failed:',
                fallbackError
              );
          }
        }

        // Publish error event
        publishEvent(EVENTS.BUSINESS_SPACE_CALCULATION_FAILED, {
          error: error.message,
          data: data
            ? { categoryId: data.categoryId, productId: data.productId }
            : null,
          state,
          executionTime,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return {
          success: false,
          error: error.message,
          hasRequiredData: false,
        };
      }
    },

    /**
     * Cleanup method for resource management (Pattern 15)
     * Called via MODULE_SPACE_CALCULATOR_CLEANUP event
     */
    cleanup() {
      cleanup();
    },

    /**
     * Check if module is initialized (Pattern 16)
     * @returns {boolean} Whether the module is initialized
     */
    isReady() {
      return isInitialized && bootstrapComplete;
    },
  };

  // PATTERN 20: Conductor Single Publication - module published once on namespace
  isDev() && console.log('[SpaceCalculator] Module loaded');
})(window);
